"use client";

import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { storyblokEditable } from "@storyblok/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { HeroSliderStoryblok } from "@/types/sb-component-types";
import { buttonVariants } from "@/components/ui/button";
import { resolveSbLink } from "@/lib/storyblok/resolve-sb-link";
import Typography from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import { Container } from "@/components/layout/container";
import { useCurrentLocale } from "@/locales/client";
interface SbHeroSliderProps {
  blok: HeroSliderStoryblok;
}
export const SbHeroSlider = ({
  blok
}: SbHeroSliderProps) => {
  const locale = useCurrentLocale();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true
  }, [Autoplay({
    delay: 10000,
    stopOnInteraction: false
  })]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
  }, [emblaApi]);
  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);
  const PrevButton = () => <button aria-label="Previous slide" className={cn("embla__button embla__button--prev rounded-full bg-white/10 p-2 transition-colors hover:bg-white/30", !prevBtnEnabled && "opacity-50")} disabled={!prevBtnEnabled} onClick={scrollPrev} data-sentry-component="PrevButton" data-sentry-source-file="HeroSlider.tsx">
      <ChevronLeft className="h-6 w-6 text-white" data-sentry-element="ChevronLeft" data-sentry-source-file="HeroSlider.tsx" />
    </button>;
  const NextButton = () => <button aria-label="Next slide" className={cn("embla__button embla__button--next rounded-full bg-white/10 p-2 transition-colors hover:bg-white/30", !nextBtnEnabled && "opacity-50")} disabled={!nextBtnEnabled} onClick={scrollNext} data-sentry-component="NextButton" data-sentry-source-file="HeroSlider.tsx">
      <ChevronRight className="h-6 w-6 text-white" data-sentry-element="ChevronRight" data-sentry-source-file="HeroSlider.tsx" />
    </button>;
  return <div {...storyblokEditable(blok)} aria-label="Hero Image Carousel" aria-roledescription="carousel" className="embla relative h-screen" data-sentry-component="SbHeroSlider" data-sentry-source-file="HeroSlider.tsx">
      <div ref={emblaRef} className="embla__viewport h-full">
        <div className="embla__container h-full">
          {blok.hero_slides.map((slide, index) => <div key={slide._uid} aria-label={`${index + 1} of ${blok.hero_slides.length}`} aria-roledescription="slide" className="embla__slide relative flex h-full w-full items-end pb-24 pt-16 md:items-center md:py-14" role="group">
              <Image fill alt={slide.bg_image.alt || slide.title} className="z-0 object-cover" loading={index === 0 ? "eager" : "lazy"} priority={index === 0} quality={90} sizes="100vw" src={slide.bg_image?.filename} />
              <div className="absolute left-0 top-0 z-10 h-full w-full bg-gradient-to-t from-primary to-transparent md:bg-gradient-to-r" />
              <Container className="embla__slide__content relative z-20">
                <Typography className="max-w-2xl text-5xl font-bold text-white md:text-7xl" variant={index === 0 ? "h1" : "h2"}>
                  {slide.title}
                </Typography>
                {slide.subtitle && <Typography className="mt-2 max-w-lg text-lg text-white md:mt-6 md:text-xl" variant="p">
                    {slide.subtitle}
                  </Typography>}
                {slide.button_label && slide.button_link && <Link className={cn(buttonVariants({
              size: "lg",
              variant: "secondary"
            }), "mt-10")} href={resolveSbLink(slide.button_link, locale)}>
                    {slide.button_label}
                  </Link>}
              </Container>
            </div>)}
        </div>
      </div>
      {blok.hero_slides.length > 1 && <>
          <div className="absolute left-4 top-1/2 z-20 hidden -translate-y-1/2 xl:block">
            <PrevButton />
          </div>
          <div className="absolute right-4 top-1/2 z-20 hidden -translate-y-1/2 xl:block">
            <NextButton />
          </div>
          <div className="embla__dots absolute bottom-4 left-1/2 z-20 -translate-x-1/2" role="tablist">
            {scrollSnaps.map((_, index) => <button key={index} aria-label={`Go to slide ${index + 1}`} aria-selected={index === selectedIndex} className={cn("embla__dot mx-1 h-3 w-3 rounded-full bg-white/20 transition-colors hover:bg-white/30", index === selectedIndex && "is-selected")} role="tab" onClick={() => scrollTo(index)} />)}
          </div>
        </>}
    </div>;
};